import React, { useState, useEffect, useRef, ReactNode } from "react";
import { GoChevronDown } from "react-icons/go";
import classNames from "classnames";
import { FormikErrors, FormikTouched } from "formik";
import { twMerge } from "tailwind-merge";
import Skeleton from "./Skeleton";

import { DropdownOption } from "../../types";

interface DropdownProps<T> {
  id: string;
  label?: string;
  placeholder?: string;
  options?: DropdownOption[];
  value?: string;
  className?: string;
  isFetching?: boolean;
  errors?: FormikErrors<T>;
  touched?: FormikTouched<T>;
  setFieldValue: (field: string, value: string) => void;
  setFieldTouched?: (field: string, touched: boolean) => void;
}

const Dropdown = <T,>({
  id,
  label,
  placeholder,
  options = [],
  value,
  className,
  isFetching = false,
  errors = {},
  touched = {},
  setFieldValue,
  setFieldTouched,
}: DropdownProps<T>) => {
  const [isOpen, setIsOpen] = useState(false);
  const divEl = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (!divEl.current) return;
      if (!divEl.current.contains(event.target as Node)) setIsOpen(false);
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, []);

  const handleClick = () => setIsOpen(!isOpen);

  const handleOptionClick = (selectedValue: string) => {
    setIsOpen(false);
    setFieldTouched && setFieldTouched(id, true);
    setFieldValue(id, selectedValue);
  };

  const dropdownClasses = classNames(
    className,
    "w-full border border-secondary relative cursor-pointer rounded"
  );
  const key = id as keyof T;

  const inputContainerClasses = twMerge(
    classNames("flex items-center bg-white rounded p-2.5 space-evenly", {
      "border border-red-700": errors[key] && touched[key],
    })
  );
  const errorMessageClasses = "text-sm text-red-700";

  return (
    <div ref={divEl} className={dropdownClasses}>
      {label && <div className="font-semibold mb-1">{label}</div>}

      <div className={inputContainerClasses} onClick={handleClick}>
        <input
          value={value ? options.find((opt) => opt.value === value)?.label : ""}
          placeholder={placeholder}
          className="w-full bg-transparent cursor-pointer"
          readOnly
        />
        <GoChevronDown className="text-lg ml-2" />
      </div>

      {isOpen && (
        <div className="absolute top-full w-full border bg-white z-30 p-1 my-1 max-h-80 overflow-y-auto rounded shadow-md">
          {isFetching ? (
            <Skeleton className="h-10 w-full my-1" times={3} />
          ) : (
            options.map((opt) => (
              <div
                key={opt.value}
                className="p-2 hover:bg-gray-200 cursor-pointer rounded"
                onClick={() => handleOptionClick(opt.value)}
              >
                {opt.label}
              </div>
            ))
          )}
        </div>
      )}

      {errors[id as keyof T] && touched[id as keyof T] && (
        <div className={errorMessageClasses}>
          {errors[id as keyof T] as ReactNode}
        </div>
      )}
    </div>
  );
};

export default Dropdown;
